import type { SEOData } from '@/components/feature/SEOHead';

export interface Category {
  id: string;
  name: string;
  description: string;
  coverImage: string;
  works: Work[];
}

export interface Work {
  id: string;
  title: string;
  category: string;
  image: string;
  description: string;
}

export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  coverImage: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
}

export interface Testimonial {
  id: string;
  name: string;
  avatar: string;
  rating: number;
  content: string;
  service: string;
}

export interface FAQ {
  question: string;
  answer: string;
}

export const siteInfo = {
  name: '秀人写真',
  tagline: '专业人像摄影定制服务',
  description: '秀人写真为您提供专业人像摄影服务，涵盖个人写真、情侣写真、艺术写真等多种风格。专业摄影师团队，精修后期处理，打造属于您的独家视觉作品，留住每一个珍贵瞬间。',
  phone: '400-888-9999',
  email: 'hello@xiurenphoto.com',
  address: '北京市朝阳区建国路88号SOHO现代城A座1208室',
  wechat: 'xiurenphoto',
  weibo: '@秀人写真',
  workingHours: '周一至周日 09:00 - 21:00',
};

export const categories: Category[] = [
  {
    id: 'personal',
    name: '个人写真',
    description: '捕捉最真实的你，展现独特个性魅力。从清新自然到时尚摩登，多元风格满足你的每一种想象。',
    coverImage: 'https://readdy.ai/api/search-image?query=Professional%20portrait%20photography%20of%20a%20young%20woman%20in%20natural%20light%2C%20soft%20elegant%20style%2C%20clean%20cream%20background%2C%20minimalist%20aesthetic%2C%20fashion%20editorial%20look%2C%20warm%20natural%20tones%2C%20shallow%20depth%20of%20field%2C%20professional%20studio%20lighting%2C%20beautiful%20soft%20shadows&width=800&height=600&seq=category-personal-01&orientation=landscape',
    works: [
      { id: 'p1', title: '春日序曲', category: 'personal', image: 'https://readdy.ai/api/search-image?query=Elegant%20young%20woman%20portrait%20in%20spring%20garden%2C%20soft%20natural%20light%20through%20cherry%20blossoms%2C%20cream%20dress%2C%20dreamy%20atmosphere%2C%20professional%20fashion%20photography%2C%20warm%20pastel%20tones%2C%20shallow%20depth%20of%20field%2C%20clean%20composition&width=600&height=800&seq=work-p1&orientation=portrait', description: '春日花园外景拍摄，轻盈自然的个人写真' },
      { id: 'p2', title: '都市律动', category: 'personal', image: 'https://readdy.ai/api/search-image?query=Modern%20urban%20portrait%20of%20young%20woman%20in%20city%20street%2C%20fashionable%20casual%20outfit%2C%20soft%20sunset%20golden%20hour%20light%2C%20clean%20architectural%20background%2C%20professional%20editorial%20style%2C%20warm%20neutral%20tones%2C%20minimalist%20composition&width=600&height=800&seq=work-p2&orientation=portrait', description: '都市街拍风格，展现现代女性的独立自信' },
      { id: 'p3', title: '静谧时光', category: 'personal', image: 'https://readdy.ai/api/search-image?query=Intimate%20indoor%20portrait%20of%20young%20woman%20by%20window%2C%20soft%20diffused%20natural%20light%2C%20cream%20linen%20outfit%2C%20minimalist%20clean%20interior%2C%20warm%20cozy%20atmosphere%2C%20professional%20lifestyle%20photography%2C%20gentle%20expression%2C%20soft%20beige%20tones&width=600&height=800&seq=work-p3&orientation=portrait', description: '室内暖光氛围感写真，记录静谧温柔时刻' },
    ],
  },
  {
    id: 'couple',
    name: '情侣写真',
    description: '定格你们的甜蜜瞬间，用镜头讲述专属的爱情故事。每一帧都是爱的见证，让美好成为永恒。',
    coverImage: 'https://readdy.ai/api/search-image?query=Romantic%20couple%20photography%20in%20golden%20sunset%20field%2C%20tender%20embrace%2C%20soft%20warm%20light%2C%20natural%20flowing%20outfits%2C%20dreamy%20romantic%20atmosphere%2C%20professional%20wedding%20style%20photography%2C%20clean%20cream%20background%20tones%2C%20shallow%20depth%20of%20field&width=800&height=600&seq=category-couple-01&orientation=landscape',
    works: [
      { id: 'c1', title: '落日余晖', category: 'couple', image: 'https://readdy.ai/api/search-image?query=Romantic%20couple%20portrait%20at%20sunset%20beach%2C%20golden%20warm%20light%2C%20tender%20embrace%2C%20flowing%20white%20dress%2C%20natural%20candid%20moment%2C%20professional%20wedding%20photography%20style%2C%20soft%20romantic%20atmosphere%2C%20clean%20dreamy%20composition&width=600&height=800&seq=work-c1&orientation=portrait', description: '海边落日情侣写真，浪漫与温柔并存' },
      { id: 'c2', title: '城市恋人', category: 'couple', image: 'https://readdy.ai/api/search-image?query=Stylish%20couple%20portrait%20in%20modern%20city%20cafe%2C%20natural%20window%20light%2C%20casual%20chic%20outfits%2C%20intimate%20candid%20moment%2C%20professional%20lifestyle%20photography%2C%20warm%20cozy%20tones%2C%20clean%20minimalist%20composition&width=600&height=800&seq=work-c2&orientation=portrait', description: '城市咖啡馆情侣写真，记录日常甜蜜' },
      { id: 'c3', title: '森系恋歌', category: 'couple', image: 'https://readdy.ai/api/search-image?query=Romantic%20couple%20portrait%20in%20enchanted%20forest%2C%20soft%20dappled%20sunlight%20through%20trees%2C%20natural%20earthy%20outfits%2C%20dreamy%20fairytale%20atmosphere%2C%20professional%20outdoor%20photography%2C%20warm%20green%20and%20cream%20tones&width=600&height=800&seq=work-c3&orientation=portrait', description: '森林系情侣写真，自然与爱的完美融合' },
    ],
  },
  {
    id: 'artistic',
    name: '艺术写真',
    description: '突破常规，探索影像的无限可能。创意构图、光影艺术，为你打造电影级的视觉大片。',
    coverImage: 'https://readdy.ai/api/search-image?query=Artistic%20creative%20portrait%20photography%20with%20dramatic%20lighting%2C%20avant%20garde%20fashion%20style%2C%20bold%20shadows%20and%20highlights%2C%20creative%20composition%2C%20museum%20quality%20fine%20art%20aesthetic%2C%20warm%20neutral%20cream%20background%2C%20professional%20studio%20lighting&width=800&height=600&seq=category-artistic-01&orientation=landscape',
    works: [
      { id: 'a1', title: '光影诗篇', category: 'artistic', image: 'https://readdy.ai/api/search-image?query=Dramatic%20artistic%20portrait%20with%20creative%20lighting%2C%20bold%20shadow%20patterns%20on%20face%2C%20black%20and%20white%20fine%20art%20style%2C%20professional%20studio%20photography%2C%20strong%20contrast%2C%20emotional%20expression%2C%20clean%20minimalist%20composition&width=600&height=800&seq=work-a1&orientation=portrait', description: '黑白光影艺术写真，极简中的极致表达' },
      { id: 'a2', title: '色彩狂想', category: 'artistic', image: 'https://readdy.ai/api/search-image?query=Creative%20colorful%20fashion%20portrait%20with%20bold%20artistic%20makeup%2C%20vibrant%20warm%20color%20palette%2C%20creative%20studio%20lighting%2C%20editorial%20magazine%20style%2C%20professional%20fine%20art%20photography%2C%20striking%20visual%20impact&width=600&height=800&seq=work-a2&orientation=portrait', description: '大胆色彩创意写真，视觉冲击力十足' },
      { id: 'a3', title: '古典新韵', category: 'artistic', image: 'https://readdy.ai/api/search-image?query=Elegant%20Chinese%20classical%20style%20portrait%20with%20modern%20twist%2C%20traditional%20hanfu%20dress%2C%20soft%20studio%20lighting%2C%20ink%20wash%20painting%20aesthetic%2C%20professional%20fine%20art%20photography%2C%20warm%20neutral%20cream%20tones&width=600&height=800&seq=work-a3&orientation=portrait', description: '国风艺术写真，古典美学与现代摄影的碰撞' },
    ],
  },
  {
    id: 'family',
    name: '亲子写真',
    description: '记录家庭的温暖时光，定格孩子成长的每一个珍贵瞬间。充满爱的画面，是最美的作品。',
    coverImage: 'https://readdy.ai/api/search-image?query=Warm%20family%20portrait%20photography%20in%20sunlit%20meadow%2C%20parents%20and%20young%20child%20laughing%20naturally%2C%20soft%20golden%20hour%20light%2C%20casual%20cream%20and%20beige%20outfits%2C%20joyful%20candid%20moment%2C%20professional%20lifestyle%20photography%2C%20clean%20natural%20composition&width=800&height=600&seq=category-family-01&orientation=landscape',
    works: [
      { id: 'f1', title: '幸福三重奏', category: 'family', image: 'https://readdy.ai/api/search-image?query=Happy%20family%20of%20three%20portrait%20in%20park%2C%20parents%20holding%20toddler%2C%20genuine%20smiles%2C%20soft%20natural%20sunlight%2C%20casual%20cream%20toned%20outfits%2C%20professional%20family%20photography%2C%20warm%20joyful%20atmosphere&width=600&height=800&seq=work-f1&orientation=portrait', description: '三口之家亲子写真，幸福写在每一个笑容里' },
      { id: 'f2', title: '母爱如光', category: 'family', image: 'https://readdy.ai/api/search-image?query=Tender%20mother%20and%20baby%20portrait%20by%20window%2C%20soft%20diffused%20natural%20light%2C%20cream%20linen%20outfits%2C%20intimate%20bonding%20moment%2C%20professional%20newborn%20photography%20style%2C%20warm%20gentle%20atmosphere&width=600&height=800&seq=work-f2&orientation=portrait', description: '母婴写真，记录母爱最温柔的模样' },
      { id: 'f3', title: '童趣时光', category: 'family', image: 'https://readdy.ai/api/search-image?query=Playful%20children%20portrait%20in%20sunlit%20garden%2C%20young%20siblings%20laughing%20and%20playing%2C%20natural%20candid%20moment%2C%20soft%20warm%20light%2C%20colorful%20cream%20outfits%2C%20professional%20kids%20photography%2C%20joyful%20atmosphere&width=600&height=800&seq=work-f3&orientation=portrait', description: '儿童写真，捕捉童年最纯真的快乐' },
    ],
  },
];

export const featuredWorks: Work[] = [
  categories[0].works[0],
  categories[1].works[0],
  categories[2].works[0],
  categories[3].works[0],
  categories[0].works[1],
  categories[1].works[1],
  categories[2].works[1],
  categories[3].works[1],
];

export const allWorks: Work[] = categories.flatMap((c) => c.works);

export const testimonials: Testimonial[] = [
  { id: 't1', name: '林小雅', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20young%20Chinese%20woman%2C%20warm%20smile%2C%20clean%20cream%20background%2C%20soft%20studio%20lighting%2C%20minimalist%20style&width=100&height=100&seq=avatar-t1&orientation=squarish', rating: 5, content: '第一次拍写真就选择了秀人写真，整个体验远超预期！摄影师非常专业，引导动作很自然，完全不尴尬。成片效果太惊艳了，每张都想洗出来挂墙上。已经推荐给身边所有朋友了！', service: '个人写真' },
  { id: 't2', name: '陈思远', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20young%20Chinese%20man%2C%20friendly%20smile%2C%20clean%20cream%20background%2C%20soft%20studio%20lighting%2C%20minimalist%20style&width=100&height=100&seq=avatar-t2&orientation=squarish', rating: 5, content: '和女朋友拍了情侣写真，摄影师把我们最自然的互动瞬间都捕捉到了。选片的时候每一张都舍不得删，后期精修也特别细致。这是我们在一起最好的纪念！', service: '情侣写真' },
  { id: 't3', name: '王芳菲', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20middle%20aged%20Chinese%20woman%2C%20gentle%20smile%2C%20clean%20cream%20background%2C%20soft%20studio%20lighting%2C%20minimalist%20style&width=100&height=100&seq=avatar-t3&orientation=squarish', rating: 5, content: '带孩子来拍亲子写真，摄影师特别有耐心，孩子一开始有点认生，摄影师用了各种方法逗孩子开心。最后出来的照片太有爱了，把我们的幸福都记录了下来。', service: '亲子写真' },
  { id: 't4', name: '张明宇', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20young%20Chinese%20man%2C%20confident%20look%2C%20clean%20cream%20background%2C%20soft%20studio%20lighting%2C%20minimalist%20style&width=100&height=100&seq=avatar-t4&orientation=squarish', rating: 5, content: '艺术写真超出了我的想象！摄影师对光影的把控堪称大师级，拍出来的照片像电影海报一样。后期修图师也非常用心，每一处细节都处理得恰到好处。', service: '艺术写真' },
  { id: 't5', name: '刘诗涵', avatar: 'https://readdy.ai/api/search-image?query=Professional%20headshot%20of%20young%20Chinese%20woman%2C%20elegant%20smile%2C%20clean%20cream%20background%2C%20soft%20studio%20lighting%2C%20minimalist%20style&width=100&height=100&seq=avatar-t5&orientation=squarish', rating: 5, content: '已经是第二次来拍了！第一次是个人写真，这次带了闺蜜一起来。每次都给我不一样的惊喜，风格多变但品质始终在线。秀人写真真的是写真界的天花板！', service: '个人写真' },
];

export const articles: Article[] = [
  {
    slug: 'how-to-prepare-for-photoshoot',
    title: '写真拍摄前的准备指南：从穿搭到心态的全方位攻略',
    excerpt: '第一次拍写真不知道该怎么准备？别担心，这篇详细指南从服装搭配、妆容建议到心态调整，帮你做好万全准备，轻松拍出满意大片。',
    content: `<p>拍一组美美的写真，是很多人期待已久的事情。但第一次面对镜头时，紧张和不自然是难免的。今天就为大家详细讲解写真拍摄前的准备工作，让你轻松应对拍摄日。</p>
<h2>一、服装搭配：选对衣服成功一半</h2>
<p>服装是写真拍摄中最关键的元素之一。建议准备2-3套不同风格的服装，涵盖日常休闲、优雅正式和个性创意三个方向。</p>
<p><strong>颜色选择：</strong>建议选择纯色或简单图案的服装，避免过于复杂的印花。米色、白色、浅灰、深棕等中性色调是万能选择，在镜头前永远不会出错。</p>
<p><strong>版型要点：</strong>选择能够修饰身材的版型，突出自身优势。A字裙、高腰裤、V领上衣都是不错的选择。</p>
<h2>二、妆容与发型</h2>
<p>写真妆容比日常妆稍重一些，但切忌过于浓艳。建议以自然清透的底妆为基础，重点突出眼妆和唇妆。</p>
<p>发型方面，保持头发清洁蓬松即可。如果计划多个造型，可以提前准备发饰或假发片。</p>
<h2>三、心态调整最重要</h2>
<p>很多人面对镜头会紧张僵硬，这是完全正常的。记住几个小技巧：深呼吸放松、多和摄影师沟通、把镜头当作朋友而不是考官。</p>
<p>我们的摄影师会全程引导你的动作和表情，你只需要保持轻松愉悦的心情，相信专业的力量。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Professional%20photography%20studio%20setup%20with%20camera%20equipment%2C%20soft%20natural%20light%2C%20elegant%20cream%20interior%2C%20minimalist%20aesthetic%2C%20warm%20atmosphere%2C%20clean%20composition&width=800&height=500&seq=article-prep-01&orientation=landscape',
    category: '拍摄指南',
    author: '秀人写真摄影团队',
    date: '2026-08-05',
    readTime: '8分钟',
    tags: ['拍摄准备', '穿搭指南', '写真技巧'],
  },
  {
    slug: 'portrait-photography-tips',
    title: '人像摄影技巧：如何拍出自然又好看的照片',
    excerpt: '专业摄影师分享的实用人像拍摄技巧，从光线利用到角度选择，教你如何在镜头前展现最美的自己。',
    content: `<p>你是否经常觉得自己的照片不够好看？其实，拍出好看的照片并不需要你是超模，掌握一些实用的拍摄技巧，每个人都能在镜头前发光。</p>
<h2>一、光线的魔力</h2>
<p>光线是摄影的灵魂。自然光是最佳选择，尤其是清晨和傍晚的柔和光线，能让皮肤显得更加细腻光滑。避免正午强烈的顶光，它会在脸上产生不自然的阴影。</p>
<p>在室内拍摄时，尽量靠近窗户，利用侧光塑造立体感。</p>
<h2>二、找到你的最佳角度</h2>
<p>每个人的脸部特征不同，找到适合自己角度的关键是多尝试。通常微微侧脸45度角是最上镜的角度，可以让脸部线条更加流畅。</p>
<h2>三、自然表情的秘密</h2>
<p>与其刻意摆拍，不如尝试在动态中抓拍。走路、转身、撩头发这些自然动作往往能捕捉到最真实的美丽瞬间。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Professional%20photographer%20shooting%20portrait%20in%20studio%2C%20soft%20natural%20lighting%20setup%2C%20cream%20neutral%20background%2C%20elegant%20atmosphere%2C%20minimalist%20photography%20studio%2C%20warm%20tones&width=800&height=500&seq=article-tips-01&orientation=landscape',
    category: '摄影技巧',
    author: '秀人写真首席摄影师 - 李明',
    date: '2026-07-28',
    readTime: '6分钟',
    tags: ['人像摄影', '拍照技巧', '光线运用'],
  },
  {
    slug: 'best-seasons-for-photoshoot',
    title: '一年四季写真拍摄推荐：每个季节都有独特的美',
    excerpt: '不同季节的写真拍摄各有千秋，春天花海浪漫、夏天阳光明媚、秋天色彩斑斓、冬天纯净唯美。来看看哪个季节最适合你。',
    content: `<p>写真拍摄不分季节，每个季节都有独特的魅力和拍摄优势。今天就来详细介绍一下四季拍摄的特点和注意事项。</p>
<h2>春 · 万物复苏的浪漫</h2>
<p>春天是拍写真的黄金季节。樱花、桃花、油菜花次第开放，天然的花海背景让照片充满生机。建议选择粉色、白色等清新色系的服装。</p>
<h2>夏 · 阳光灿烂的活力</h2>
<p>夏天的光线充足，适合外景拍摄。海滩、泳池、草地都是绝佳的拍摄场景。建议选择清凉透气的服装，注意防晒和补水。</p>
<h2>秋 · 色彩斑斓的文艺</h2>
<p>秋天的色彩最为丰富，枫叶、银杏、芦苇都是绝佳的背景元素。建议选择暖色调服装，与秋色完美呼应。</p>
<h2>冬 · 纯净唯美的浪漫</h2>
<p>冬天的雪景写真梦幻唯美。室内暖光主题拍摄也是冬季的热门选择，营造温馨舒适的氛围感。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Beautiful%20four%20seasons%20collage%20style%20photography%2C%20spring%20cherry%20blossom%20summer%20beach%20autumn%20maple%20winter%20snow%2C%20soft%20natural%20light%2C%20artistic%20composition%2C%20warm%20elegant%20tones%2C%20professional%20landscape%20portrait%20style&width=800&height=500&seq=article-season-01&orientation=landscape',
    category: '拍摄指南',
    author: '秀人写真摄影团队',
    date: '2026-07-20',
    readTime: '7分钟',
    tags: ['季节拍摄', '外景写真', '拍摄建议'],
  },
  {
    slug: 'post-processing-secrets',
    title: '写真后期精修的奥秘：自然美才是最高级的美',
    excerpt: '很多客户担心后期修图会过度失真。其实专业写真后期追求的是"修了像没修"的自然效果，保留真实的同时提升质感。',
    content: `<p>写真后期精修是整个拍摄流程中不可或缺的一环。但很多人对后期修图存在误解，以为修图就是把脸P得面目全非。其实，专业写真后期追求的是自然质感的提升。</p>
<h2>一、后期修图的核心原则</h2>
<p>我们的修图理念是"修了像没修"。保留你原本的五官特征和皮肤质感，只做细微的优化调整：均匀肤色、淡化细纹、优化光影层次。</p>
<h2>二、修图包含哪些内容</h2>
<p>专业的写真后期包括：基础调色、皮肤优化、身材微调、背景处理、整体氛围营造等。每一处调整都经过精心考量，确保整体效果自然和谐。</p>
<h2>三、如何与修图师沟通</h2>
<p>我们建议你在选片后与修图师充分沟通你的需求。比如你希望保留的特色、不喜欢的角度、偏好的色调风格等，这些信息能帮助修图师更好地理解你的审美偏好。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Professional%20photo%20editing%20workstation%20with%20monitor%20displaying%20portrait%20retouching%2C%20graphic%20tablet%2C%20minimalist%20cream%20interior%2C%20soft%20warm%20lighting%2C%20creative%20workspace%2C%20clean%20elegant%20style&width=800&height=500&seq=article-post-01&orientation=landscape',
    category: '后期修图',
    author: '秀人写真后期总监 - 张悦',
    date: '2026-07-15',
    readTime: '5分钟',
    tags: ['后期精修', '修图技巧', '自然美'],
  },
  {
    slug: 'couple-photoshoot-ideas',
    title: '情侣写真创意拍摄方案：15个超甜Pose合集',
    excerpt: '不知道情侣写真该怎么摆姿势？这里有15个超实用的拍摄创意，从经典拥抱到趣味互动，让你们的甜蜜溢出屏幕。',
    content: `<p>情侣写真是记录爱情最美好的方式之一。但面对镜头时，很多人不知道该怎么互动才自然。今天就来分享一些超实用的情侣拍摄姿势和创意。</p>
<h2>一、经典必拍Pose</h2>
<p>额头相抵、背后拥抱、牵手漫步这些经典姿势永远不会过时。关键在于放松自然，不要过于僵硬。摄影师会在旁边引导你们调整到最舒适的角度。</p>
<h2>二、互动抓拍更有爱</h2>
<p>与其摆拍，不如做一些真实的互动：讲悄悄话、互相整理头发、一起大笑。这些自然流露的瞬间往往是最动人的。</p>
<h2>三、场景化创意拍摄</h2>
<p>可以根据你们的相识故事设计场景化拍摄。比如在第一次约会的咖啡馆、最喜欢的书店、常去的公园等有特殊意义的地方拍摄。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Romantic%20couple%20photography%20with%20creative%20poses%20in%20urban%20setting%2C%20natural%20candid%20interaction%2C%20soft%20golden%20hour%20light%2C%20casual%20stylish%20outfits%2C%20warm%20romantic%20atmosphere%2C%20professional%20editorial%20style&width=800&height=500&seq=article-couple-01&orientation=landscape',
    category: '拍摄创意',
    author: '秀人写真摄影团队',
    date: '2026-07-08',
    readTime: '6分钟',
    tags: ['情侣写真', '拍摄创意', 'Pose指南'],
  },
  {
    slug: 'choosing-photography-package',
    title: '如何选择适合自己的写真套餐？一篇讲清楚',
    excerpt: '面对各种写真套餐不知道怎么选？从预算、风格、场景三个维度帮你分析，找到最适合你的拍摄方案。',
    content: `<p>写真套餐种类繁多，价格从几百到几千不等，如何才能选到最适合自己的那一个呢？今天就来详细分析一下。</p>
<h2>一、明确你的需求</h2>
<p>首先要明确拍写真的目的：是纪念重要时刻、送给自己的礼物还是用于社交展示？不同目的对应不同的拍摄风格和预算。</p>
<h2>二、了解套餐构成</h2>
<p>写真套餐通常包含：拍摄时长、服装造型套数、精修张数、底片数量、相册与相框等产品。根据自己的需求选择合适的配置。</p>
<h2>三、预算建议</h2>
<p>入门级套餐适合体验型客户，中端套餐性价比最高，高端定制套餐适合对品质有极高要求的客户。我们建议首次拍摄可以选择中端套餐，体验全面又不会过于昂贵。</p>`,
    coverImage: 'https://readdy.ai/api/search-image?query=Elegant%20photography%20studio%20consultation%20area%2C%20minimalist%20cream%20interior%2C%20photo%20albums%20and%20prints%20on%20display%2C%20soft%20warm%20lighting%2C%20professional%20clean%20atmosphere&width=800&height=500&seq=article-package-01&orientation=landscape',
    category: '选购指南',
    author: '秀人写真客服顾问',
    date: '2026-06-30',
    readTime: '5分钟',
    tags: ['写真套餐', '选购指南', '性价比'],
  },
];

export const faqs: FAQ[] = [
  {
    question: '拍摄前需要做哪些准备？',
    answer: '建议您提前一周做好皮肤护理，拍摄前一天保证充足睡眠。服装方面，可以准备2-3套不同风格的服装，我们会根据您的风格特点给出专业建议。拍摄当天保持轻松愉快的心情即可，我们的摄影师会全程引导。',
  },
  {
    question: '写真拍摄一般需要多长时间？',
    answer: '根据套餐不同，拍摄时间在2-6小时不等。个人写真基础套餐约2-3小时，情侣写真约3-4小时，艺术写真和定制套餐约4-6小时。其中包含化妆造型和换装时间。',
  },
  {
    question: '精修照片多久可以拿到？',
    answer: '拍摄结束后3个工作日内，我们会提供全部底片供您选片。选定精修照片后，精修周期为7-10个工作日。加急服务可在5个工作日内完成（需额外支付加急费用）。',
  },
  {
    question: '可以自己选择拍摄地点吗？',
    answer: '当然可以！我们支持棚内拍摄和外景拍摄。如果您有心仪的拍摄地点，可以提前告知我们，摄影师会进行实地考察并提供拍摄建议。热门外景地包括公园、咖啡馆、特色街区等。',
  },
  {
    question: '拍摄不满意可以重拍吗？',
    answer: '我们对自己的拍摄品质充满信心。如果因我方原因导致拍摄效果不理想，我们承诺免费重拍。拍摄当天您可以实时查看照片效果，如有不满意的地方可以及时调整。',
  },
  {
    question: '写真套餐的价格范围是多少？',
    answer: '我们的写真套餐从￥999的基础体验套餐到￥6999的高端定制套餐不等，涵盖不同需求和预算。具体套餐详情和最新优惠，欢迎通过表单或电话咨询我们的客服顾问。',
  },
];

export const aboutInfo = {
  brandStory: `秀人写真成立于2018年，由一群热爱摄影的年轻人创办。我们相信，每个人都有独一无二的美，而我们的使命就是用镜头发现并记录这份美。

从最初的三人小团队，到如今拥有20+专业摄影师和后期团队，秀人写真已经服务了超过5000位客户。每一位客户的信任和笑容，都是我们不断前行的动力。

我们坚持用专业的技术、真诚的服务和创新的理念，为每一位客户打造专属的视觉作品。不追求千篇一律的美，只做属于你的独家记忆。`,
  philosophy: [
    { title: '专业至上', description: '每一位摄影师都经过严格筛选和专业培训，拥有丰富的拍摄经验和独特的审美视角。' },
    { title: '真诚服务', description: '从预约咨询到成品交付，全程一对一专属服务，用心倾听你的每一个需求。' },
    { title: '创新为魂', description: '持续探索最新的拍摄技术和风格趋势，让你的写真始终走在时尚前沿。' },
    { title: '品质承诺', description: '对每一张照片精益求精，不满意免费重拍，这是我们对自己的信心也是对客户的承诺。' },
  ],
  stats: [
    { number: '5000+', label: '服务客户' },
    { number: '20+', label: '专业摄影师' },
    { number: '98%', label: '好评率' },
    { number: '6年', label: '行业经验' },
  ],
  teamPhotos: [
    'https://readdy.ai/api/search-image?query=Professional%20photographer%20portrait%20in%20studio%2C%20holding%20camera%2C%20creative%20professional%20look%2C%20warm%20lighting%2C%20clean%20cream%20background%2C%20minimalist%20style&width=400&height=500&seq=team-01&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Professional%20female%20photographer%20portrait%2C%20confident%20smile%2C%20modern%20studio%20setting%2C%20warm%20lighting%2C%20clean%20cream%20background%2C%20minimalist%20professional%20style&width=400&height=500&seq=team-02&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Professional%20photo%20editor%20at%20workstation%2C%20creative%20professional%2C%20warm%20studio%20lighting%2C%20clean%20cream%20background%2C%20minimalist%20style&width=400&height=500&seq=team-03&orientation=portrait',
    'https://readdy.ai/api/search-image?query=Professional%20makeup%20artist%20portrait%2C%20creative%20professional%2C%20warm%20studio%20lighting%2C%20clean%20cream%20background%2C%20minimalist%20elegant%20style&width=400&height=500&seq=team-04&orientation=portrait',
  ],
};

export const packages = [
  {
    name: '基础体验',
    price: '999',
    features: ['2小时拍摄时长', '2套服装造型', '15张精修', '全部底片赠送', '1个拍摄场景', '7天出片'],
    popular: false,
  },
  {
    name: '经典优选',
    price: '2,399',
    features: ['3小时拍摄时长', '3套服装造型', '30张精修', '全部底片赠送', '2个拍摄场景', '10天出片', '精美相册一本'],
    popular: true,
  },
  {
    name: '尊享定制',
    price: '4,999',
    features: ['5小时拍摄时长', '5套服装造型', '50张精修', '全部底片赠送', '3个拍摄场景', '10天出片', '高级相册一本', '24寸放大照片', '拍摄花絮视频'],
    popular: false,
  },
];

export const homeSEO: SEOData = {
  title: '秀人写真 | 专业人像摄影定制服务 - 个人写真·情侣写真·艺术写真',
  description: '秀人写真为您提供专业人像摄影服务，涵盖个人写真、情侣写真、艺术写真等多种风格。专业摄影师团队，精修后期处理，打造属于您的独家秀人写真视觉作品，留住每一个珍贵瞬间。',
  keywords: '秀人写真,秀人写真摄影,个人写真拍摄,专业写真定制,艺术人像写真,写真后期精修,写真套餐价格',
  canonicalUrl: '/',
  ogType: 'website',
};

export const categorySEO: Record<string, SEOData> = {
  personal: {
    title: '个人写真 | 秀人写真 - 专业个人写真拍摄定制服务',
    description: '秀人写真个人写真服务，捕捉最真实的你。专业摄影师一对一服务，清新自然到时尚摩登多元风格，精修后期处理，打造你的专属个人写真大片。',
    keywords: '个人写真,个人写真拍摄,个人写真摄影,个人艺术照,写真定制',
    canonicalUrl: '/category?type=personal',
  },
  couple: {
    title: '情侣写真 | 秀人写真 - 浪漫情侣写真拍摄服务',
    description: '秀人写真情侣写真服务，定格你们的甜蜜瞬间。专业情侣写真摄影师，城市街拍到自然外景多元场景，记录专属你们的爱情故事。',
    keywords: '情侣写真,情侣照拍摄,情侣艺术照,浪漫写真,情侣摄影',
    canonicalUrl: '/category?type=couple',
  },
  artistic: {
    title: '艺术写真 | 秀人写真 - 创意艺术人像写真拍摄',
    description: '秀人写真艺术写真服务，突破常规探索影像无限可能。创意构图光影艺术，为你打造电影级视觉大片，高端艺术人像写真定制。',
    keywords: '艺术写真,艺术人像写真,创意写真,高端写真,艺术照拍摄',
    canonicalUrl: '/category?type=artistic',
  },
  family: {
    title: '亲子写真 | 秀人写真 - 温暖亲子家庭写真拍摄',
    description: '秀人写真亲子写真服务，记录家庭温暖时光。专业亲子摄影师，捕捉孩子成长的珍贵瞬间，充满爱的画面是最美的作品。',
    keywords: '亲子写真,家庭写真,亲子照,儿童写真,全家福拍摄',
    canonicalUrl: '/category?type=family',
  },
};

export const aboutSEO: SEOData = {
  title: '关于我们 | 秀人写真 - 专业人像摄影品牌',
  description: '了解秀人写真品牌故事、专业摄影团队、服务理念与客户口碑。秀人写真成立于2018年，已服务5000+客户，专业人像摄影定制服务品牌。',
  keywords: '秀人写真品牌,摄影团队,写真摄影工作室,秀人写真评价,摄影品牌介绍',
  canonicalUrl: '/about',
};

export const articlesSEO: SEOData = {
  title: '摄影指南 | 秀人写真 - 写真拍摄技巧与行业资讯',
  description: '秀人写真摄影指南，涵盖拍摄准备、人像摄影技巧、季节拍摄推荐、后期精修知识、写真套餐选购指南等专业摄影资讯。',
  keywords: '写真拍摄技巧,人像摄影指南,写真准备攻略,摄影教程,写真套餐选购',
  canonicalUrl: '/articles',
};